import React from 'react';
import { Helmet } from 'react-helmet-async';

export const SEO = ({
  title,
  description,
  canonical,
  ogImage = 'https://idaki.de/images/ida-7.svg',
  ogType = 'website',
  structuredData = null
}) => {
  const siteTitle = 'IDA - Interne Daten Assistentin';
  const fullTitle = title ? `${title} | ${siteTitle}` : `${siteTitle} - Compliance Software für Spielhallen, Wettbüros & Gastronomie`;
  const defaultDescription = 'IDA vereinfacht Compliance für Spielhallen, Wettbüros und Gastronomie. Spielerschutz, Jugendschutz, HACCP und Geldwäsche-Prävention – digital, sicher und DSGVO-konform.';
  const metaDescription = description || defaultDescription;
  const canonicalUrl = canonical || 'https://idaki.de/';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:type" content={ogType} />

      {/* Twitter */}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Predefined SEO configurations for common pages
export const SEOConfig = {
  homepage: {
    title: null, // Will use default from index.html
    description: 'IDA vereinfacht Compliance für Spielhallen, Wettbüros und Gastronomie. Spielerschutz, Jugendschutz, HACCP und Geldwäsche-Prävention – digital, sicher und DSGVO-konform.',
    canonical: 'https://idaki.de/',
  },
  login: {
    title: 'Login',
    description: 'Melden Sie sich bei IDA an, um auf Ihre Compliance-Dashboards und Funktionen zuzugreifen.',
    canonical: 'https://idaki.de/login',
  },
  register: {
    title: 'Registrierung',
    description: 'Registrieren Sie sich für IDA und starten Sie mit professionellem Compliance-Management für Ihr Unternehmen.',
    canonical: 'https://idaki.de/register',
  },
  riskReward: {
    title: 'Risiko & Reward Selbsttest',
    description: 'Entdecken Sie Ihr persönliches Risikoprofil mit unserem interaktiven Kartenspiel. 50 strategische Entscheidungen analysieren Ihr Spielverhalten.',
    canonical: 'https://idaki.de/risk-reward',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Game",
      "name": "Risiko & Reward Selbsttest",
      "description": "Interaktives Kartenspiel zur Analyse des persönlichen Risikoprofils",
      "url": "https://idaki.de/risk-reward",
      "gamePlatform": "Web Browser",
      "genre": "Educational"
    }
  },
  impressum: {
    title: 'Impressum',
    description: 'Impressum und rechtliche Informationen von IDA KI.',
    canonical: 'https://idaki.de/impressum',
  },
  agb: {
    title: 'AGB',
    description: 'Allgemeine Geschäftsbedingungen von IDA KI.',
    canonical: 'https://idaki.de/agb',
  },
  datenschutz: {
    title: 'Datenschutzerklärung',
    description: 'Datenschutzerklärung der IDA Compliance Software. DSGVO-konform, keine Cookies, keine Tracking-Tools. Server in Deutschland.',
    canonical: 'https://idaki.de/datenschutz',
  },
  avv: {
    title: 'Auftragsverarbeitungsvereinbarung (AVV)',
    description: 'AVV gemäß Art. 28 DSGVO für die Nutzung der IDA SaaS-Plattform. Datenschutz-konforme Auftragsverarbeitung.',
    canonical: 'https://idaki.de/avv',
  },
  nachhaltigkeit: {
    title: 'Nachhaltigkeit',
    description: '100% Ökostrom, CO2-neutral, CSRD-konform. IDA nutzt ausschließlich deutsche Rechenzentren mit Wasserkraft und höchster Energie-Effizienz.',
    canonical: 'https://idaki.de/nachhaltigkeit',
  }
};

export default SEO;
